/* Magnifissance 2026 — Made-to-order landing page
 *
 * Shared behaviour for the mto-* sections. Everything is delegated off
 * document, so sections added or re-rendered by the theme editor work with no
 * re-initialisation.
 *
 *   1. Card image dots — the design shows 3–4 dots over each card image;
 *      they cross-fade the product's first few media.
 *   2. Card wishlist — a per-browser favourite marker (localStorage). It is
 *      NOT a real wishlist: nothing is stored on the shop. See README.
 *   3. Card rails — the two "Shop the …" sections page 400px cards sideways.
 *
 * Add-to-cart is not handled here: the quick-add button submits a normal
 * form[action*="/cart/add"], which assets/cart-drawer.js already intercepts.
 */
(function () {
  'use strict';

  /* Every mto-* section renders snippets/mto-assets.liquid, so this file
     arrives once per section on the landing page — ten <script> tags, ten
     executions, ten copies of every document listener. A single heart click
     then toggled the wishlist ten times and landed back where it started,
     which is why it looked dead there but worked on the product page (that
     one loads the script through <catalog-assets>, exactly once).
     Guard the whole module instead of relying on the markup. */
  if (window.__mtoPageLoaded) return;
  window.__mtoPageLoaded = true;

  var WISHLIST_KEY = 'magni:mto:wishlist';

  /* ---------- card image arrows ----------
   *
   * The card stacks the product's first few photos; a pair of round arrows,
   * drawn on hover, steps between them and wraps at either end. Touch is left
   * alone — the row itself is the swipeable thing at that size.
   */

  function showFrame(card, index) {
    var frames = card.querySelectorAll('.mto-card__frame');
    var dots = card.querySelectorAll('.mto-card__dot');
    if (!frames.length) return;
    var i = Math.max(0, Math.min(index, frames.length - 1));

    frames.forEach(function (frame, n) {
      frame.classList.toggle('is-active', n === i);
    });
    dots.forEach(function (dot, n) {
      dot.classList.toggle('is-active', n === i);
      dot.setAttribute('aria-selected', n === i ? 'true' : 'false');
    });

    /* Without a pinned colour the frames run across every colour the product
       has, so the photo on show and the link can disagree — the card would
       read "Dark Blue" and open on whatever the product page defaults to.
       Each frame carries its colour's variant id (see mto-product-card),
       so the links move with it. Frames with no id leave the href alone. */
    var variant = frames[i].dataset.mtoFrameVariant;
    if (!variant) return;
    /* stepFrame hands this the media box, the dot handler the whole card —
       the title's link lives outside the media box, so climb to the card. */
    var root = card.closest('.mto-card') || card;
    root.querySelectorAll('[data-mto-card-href]').forEach(function (link) {
      link.href = link.dataset.mtoCardHref + '?variant=' + variant;
    });
  }

  function stepFrame(media, delta) {
    var frames = media.querySelectorAll('.mto-card__frame');
    if (frames.length < 2) return;
    var current = 0;
    frames.forEach(function (frame, n) {
      if (frame.classList.contains('is-active')) current = n;
    });
    var next = (current + delta + frames.length) % frames.length;
    showFrame(media, next);
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('[data-mto-card-nav]');
    if (!button) return;
    /* the whole image is a link to the product — stepping through its photos
       must not follow it */
    event.preventDefault();
    var media = button.closest('.mto-card__media');
    if (media) stepFrame(media, parseInt(button.dataset.mtoCardNav, 10) || 1);
  });

  /* ---------- wishlist ---------- */

  function readWishlist() {
    try {
      return JSON.parse(localStorage.getItem(WISHLIST_KEY)) || [];
    } catch (error) {
      return [];
    }
  }

  function writeWishlist(list) {
    try {
      localStorage.setItem(WISHLIST_KEY, JSON.stringify(list));
    } catch (error) {
      /* private mode / storage disabled — the toggle stays visual-only */
    }
  }

  function syncWishlist() {
    var saved = readWishlist();
    document.querySelectorAll('[data-mto-wishlist]').forEach(function (button) {
      var on = saved.indexOf(button.dataset.mtoWishlist) !== -1;
      button.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }

  function toggleWishlist(button) {
    var handle = button.dataset.mtoWishlist;
    var saved = readWishlist();
    var at = saved.indexOf(handle);
    if (at === -1) saved.push(handle);
    else saved.splice(at, 1);
    writeWishlist(saved);
    syncWishlist();
  }

  /* ---------- rails ---------- */

  /* v3's rails are the scroller itself (.mto-row) holding .mto-row__item;
     v2's were a transformed .mto-rail__track of .mto-rail__item. Measure
     whichever is there — returning 0, as this used to when it found neither,
     made every arrow click a no-op. */
  function railStep(rail) {
    var item = rail.querySelector('.mto-row__item, .mto-rail__item');
    if (!item) return rail.clientWidth;
    var box = rail.querySelector('.mto-rail__track') || rail;
    var style = getComputedStyle(box);
    var gap = parseFloat(style.columnGap) || parseFloat(style.gap) || 0;
    return item.getBoundingClientRect().width + gap;
  }

  /* Below 750px the rail is a native scroller (see mto-page.css), so the
     transform paging is off and the arrows nudge scrollLeft instead. */
  function railIsScroller(rail) {
    var overflowX = getComputedStyle(rail).overflowX;
    return overflowX === 'auto' || overflowX === 'scroll';
  }

  function railMaxIndex(rail) {
    var track = rail.querySelector('.mto-rail__track');
    var step = railStep(rail);
    if (!track || !step) return 0;
    var overflow = track.scrollWidth - rail.clientWidth;
    if (overflow <= 1) return 0;
    return Math.ceil(overflow / step);
  }

  function railArrows(rail, prevOff, nextOff) {
    var group = rail.closest('[data-mto-rail-group]') || rail.parentNode;
    var prev = group.querySelector('[data-mto-rail-prev]');
    var next = group.querySelector('[data-mto-rail-next]');
    if (prev) prev.disabled = prevOff;
    if (next) next.disabled = nextOff;
  }

  function railGo(rail, index) {
    /* A scroller has no track to transform. It also runs in a loop now — off
       the end is the start again — so neither arrow is ever dead, and the
       only thing an end means is that the next press jumps rather than
       steps. A rail with nothing to scroll keeps both arrows down. */
    if (railIsScroller(rail)) {
      var dead = rail.scrollWidth - rail.clientWidth <= 1;
      railArrows(rail, dead, dead);
      return;
    }

    var track = rail.querySelector('.mto-rail__track');
    if (!track) return;

    var max = railMaxIndex(rail);
    var i = Math.max(0, Math.min(index, max));
    rail.dataset.mtoIndex = String(i);
    track.style.transform = 'translateX(' + -(i * railStep(rail)) + 'px)';
    railArrows(rail, i <= 0, i >= max);
  }

  /* ---------- scroll indicator ---------- */

  /* The mobile board draws a 354 × 2 track with a 72-wide thumb under each
     carousel. Width and offset come from the rail's own scroll metrics, so the
     thumb means something whatever the product count is. */
  function syncScrollbar(rail) {
    var group = rail.closest('[data-mto-rail-group]');
    if (!group) return;
    var bar = group.querySelector('.mto-scrollbar');
    var thumb = bar && bar.querySelector('.mto-scrollbar__thumb');
    if (!thumb) return;

    var scrollable = rail.scrollWidth - rail.clientWidth;
    if (scrollable <= 1) {
      bar.style.visibility = 'hidden';
      return;
    }
    bar.style.visibility = '';
    var ratio = rail.clientWidth / rail.scrollWidth;
    var travel = 1 - ratio;
    var at = rail.scrollLeft / scrollable;
    thumb.style.width = (ratio * 100).toFixed(3) + '%';
    thumb.style.transform = 'translateX(' + ((at * travel * 100) / ratio).toFixed(3) + '%)';
  }

  function resetRails() {
    document.querySelectorAll('[data-mto-rail]').forEach(function (rail) {
      railGo(rail, parseInt(rail.dataset.mtoIndex, 10) || 0);
      syncScrollbar(rail);
    });
  }

  /* ---------- wiring ---------- */

  document.addEventListener('click', function (event) {
    var dot = event.target.closest('.mto-card__dot');
    if (dot) {
      event.preventDefault();
      var card = dot.closest('.mto-card');
      if (card) showFrame(card, parseInt(dot.dataset.mtoFrame, 10) || 0);
      return;
    }

    var heart = event.target.closest('[data-mto-wishlist]');
    if (heart) {
      event.preventDefault();
      toggleWishlist(heart);
      return;
    }

    var featureSeg = event.target.closest('[data-mto-feature-seg]');
    if (featureSeg) {
      event.preventDefault();
      var segFeature = featureSeg.closest('[data-mto-feature]');
      var segSlides = segFeature ? segFeature.querySelectorAll('.mto-clothes__feature-slide') : [];
      var segAt = 0;
      segSlides.forEach(function (slide, i) {
        if (slide.classList.contains('is-active')) segAt = i;
      });
      featureGo(segFeature, (parseInt(featureSeg.dataset.mtoFeatureSeg, 10) || 0) - segAt);
      return;
    }

    var featureNav = event.target.closest('[data-mto-feature-nav]');
    if (featureNav) {
      event.preventDefault();
      featureGo(featureNav.closest('[data-mto-feature]'), parseInt(featureNav.dataset.mtoFeatureNav, 10) || 1);
      return;
    }

    var arrow = event.target.closest('[data-mto-rail-prev], [data-mto-rail-next]');
    if (arrow) {
      event.preventDefault();
      var group = arrow.closest('[data-mto-rail-group]');
      var rail = group && group.querySelector('[data-mto-rail]');
      if (!rail) return;
      var dir = arrow.hasAttribute('data-mto-rail-next') ? 1 : -1;

      if (railIsScroller(rail)) {
        var end = rail.scrollWidth - rail.clientWidth;
        if (end > 1) {
          /* one card along, except off either edge, where it is the far
             edge — the row reads as a ring rather than a shelf with sides */
          var to = rail.scrollLeft + dir * railStep(rail);
          if (dir > 0 && rail.scrollLeft >= end - 1) to = 0;
          else if (dir < 0 && rail.scrollLeft <= 1) to = end;
          rail.scrollTo({ left: Math.max(0, Math.min(to, end)), behavior: 'smooth' });
        }
        window.setTimeout(function () {
          railGo(rail, 0);
          syncScrollbar(rail);
        }, 420);
        return;
      }

      var at = parseInt(rail.dataset.mtoIndex, 10) || 0;
      railGo(rail, at + dir);
    }
  });

  /* ---------- Come On In: the paged feature video ----------
     The slides and the captions are two lists that move together by index.
     Both lists always hold the same number of items, so one index addresses
     both.

     The run loops: forward from the last slide is the first, back from the
     first is the last. Both arrows are therefore always live, and the
     cross-fade is what makes the wrap bearable — a sliding track would have
     to rewind through every slide to get back to the start, where a fade
     simply arrives. */
  function featureGo(feature, dir) {
    if (!feature) return;
    var slides = feature.querySelectorAll('.mto-clothes__feature-slide');
    var captions = feature.querySelectorAll('.mto-clothes__feature-caption');
    if (slides.length < 2) return;

    var at = 0;
    slides.forEach(function (slide, i) {
      if (slide.classList.contains('is-active')) at = i;
    });
    var to = (at + dir + slides.length) % slides.length;

    slides.forEach(function (slide, i) {
      slide.classList.toggle('is-active', i === to);
      /* a slide out of view must not keep playing, or several soundtracks
         run at once behind the one the shopper is looking at */
      var video = slide.querySelector('video');
      if (!video) return;
      if (i === to) {
        if (video.autoplay) video.play().catch(function () {});
      } else {
        video.pause();
      }
    });
    captions.forEach(function (caption, i) {
      caption.classList.toggle('is-active', i === to);
    });
    /* Two sets of markers exist — the bars on the video for mobile, the ones
       under it for desktop — so these are read by the index they carry, not
       by their position in the list the query returns. */
    feature.querySelectorAll('[data-mto-feature-seg]').forEach(function (seg) {
      var on = (parseInt(seg.dataset.mtoFeatureSeg, 10) || 0) === to;
      seg.classList.toggle('is-active', on);
      seg.setAttribute('aria-selected', on ? 'true' : 'false');
    });
  }

  /* ---------- Come On In: the arrows over the video ----------
     Mobile's arrows are controls, not a one-off hint, and the run loops, so
     both of them are live on every slide. There is nothing to recompute as
     the shopper moves: this runs once, to raise them and set the lean going.
     Desktop hides these in CSS and uses the board's own arrows. */
  function startFeatureArrows() {
    document.querySelectorAll('[data-mto-feature]').forEach(function (feature) {
      if (feature.querySelectorAll('.mto-clothes__feature-slide').length < 2) return;
      feature.querySelectorAll('.mto-clothes__feature-hint').forEach(function (arrow) {
        arrow.classList.add('is-on', 'is-running');
      });
    });
  }

  /* ---------- Come On In: swipe the feature video ----------
     The arrows over the video are the visible way through the slides; the
     swipe is the one the thumb reaches for, and it goes to the same place.
     Nothing is preventDefault-ed: the gesture is judged at touchend,
     so a vertical drag scrolls the page as it always did, and a diagonal one
     is read as whichever axis moved further. */
  var featureTouch = null;

  document.addEventListener(
    'touchstart',
    function (event) {
      if (event.touches.length !== 1) {
        featureTouch = null;
        return;
      }
      var feature = event.target.closest('[data-mto-feature]');
      if (!feature) return;
      featureTouch = { feature: feature, x: event.touches[0].clientX, y: event.touches[0].clientY };
    },
    { passive: true }
  );

  document.addEventListener(
    'touchend',
    function (event) {
      var start = featureTouch;
      featureTouch = null;
      if (!start || !event.changedTouches.length) return;

      var dx = event.changedTouches[0].clientX - start.x;
      var dy = event.changedTouches[0].clientY - start.y;
      /* 40px of travel, and more of it sideways than down */
      if (Math.abs(dx) < 40 || Math.abs(dx) <= Math.abs(dy)) return;

      featureGo(start.feature, dx < 0 ? 1 : -1);
    },
    { passive: true }
  );

  document.addEventListener('touchcancel', function () { featureTouch = null; }, { passive: true });

  /* ---------- Come On In: drag the feature video with a mouse ----------
     The desk gets the same gesture as the thumb. It is judged on release,
     the way the swipe is, rather than followed live: the slides cross-fade
     instead of riding on a track, and a track is what following a cursor
     would need — the same track that would have to rewind through every
     slide to wrap from the last back to the first.

     Only a mouse: a touch already arrives as a touchstart above, and taking
     it here as well would move the slides twice for one gesture. Presses
     that land on a control are left alone, so the arrows and the markers
     still work, and a drag that travelled swallows the click it ends with,
     so dragging across a caption link never navigates. */
  var featureDrag = null;

  document.addEventListener('pointerdown', function (event) {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    var media = event.target.closest('.mto-clothes__feature-media');
    if (!media || event.target.closest('button, a')) return;
    var feature = media.closest('[data-mto-feature]');
    if (!feature || feature.querySelectorAll('.mto-clothes__feature-slide').length < 2) return;
    featureDrag = { feature: feature, media: media, x: event.clientX, y: event.clientY };
    media.classList.add('is-dragging');
  });

  document.addEventListener('pointerup', function (event) {
    var start = featureDrag;
    featureDrag = null;
    if (!start) return;
    start.media.classList.remove('is-dragging');

    var dx = event.clientX - start.x;
    var dy = event.clientY - start.y;
    if (Math.abs(dx) < 40 || Math.abs(dx) <= Math.abs(dy)) return;

    start.media.dataset.mtoDragged = '1';
    featureGo(start.feature, dx < 0 ? 1 : -1);
  });

  document.addEventListener('pointercancel', function () {
    if (featureDrag) featureDrag.media.classList.remove('is-dragging');
    featureDrag = null;
  });

  /* the click a finished drag ends with, caught before anything can act on it */
  document.addEventListener('click', function (event) {
    var media = event.target.closest('.mto-clothes__feature-media');
    if (!media || !media.dataset.mtoDragged) return;
    delete media.dataset.mtoDragged;
    event.preventDefault();
    event.stopPropagation();
  }, true);

  /* a video or image dragged out of the page is the browser's idea, not ours */
  document.addEventListener('dragstart', function (event) {
    if (event.target.closest('.mto-clothes__feature-media')) event.preventDefault();
  });

  /* ---------- Dragging the scroll indicator ----------
     The bar has only ever been a readout: syncScrollbar writes the thumb from
     the rail's scrollLeft and nothing wrote back. These handlers close the
     loop — drag the thumb, or press the track to go to that point.

     Desktop widths only. At 393 the bar is 2 tall and the row is swiped, so a
     drag target there would be both unusable and unnecessary.

     Nothing about how the bar looks changes: the hit area is a transparent
     ::after in mto-page.css, laid over it without taking part in layout. */
  var railDrag = null;

  function barIsInteractive() {
    return !window.matchMedia || window.matchMedia('(min-width: 990px)').matches;
  }

  function railOfBar(bar) {
    var group = bar.closest('[data-mto-rail-group]');
    return group && group.querySelector('[data-mto-rail]');
  }

  /* px the thumb can travel, which is what a drag is measured against */
  function thumbTravel(bar) {
    var thumb = bar.querySelector('.mto-scrollbar__thumb');
    if (!thumb) return 0;
    return bar.getBoundingClientRect().width - thumb.getBoundingClientRect().width;
  }

  document.addEventListener('pointerdown', function (event) {
    var bar = event.target.closest && event.target.closest('.mto-scrollbar');
    if (!bar || !barIsInteractive()) return;

    var rail = railOfBar(bar);
    if (!rail) return;
    var scrollable = rail.scrollWidth - rail.clientWidth;
    if (scrollable <= 1) return;

    var travel = thumbTravel(bar);
    if (travel <= 0) return;

    var thumb = bar.querySelector('.mto-scrollbar__thumb');
    var thumbRect = thumb.getBoundingClientRect();
    var onThumb = event.clientX >= thumbRect.left && event.clientX <= thumbRect.right;

    event.preventDefault();

    if (!onThumb) {
      /* A press on the track goes to that point rather than paging, and keeps
         the rail's own smooth behaviour so it eases there. */
      var barRect = bar.getBoundingClientRect();
      var at = (event.clientX - barRect.left - thumbRect.width / 2) / travel;
      rail.scrollLeft = Math.max(0, Math.min(1, at)) * scrollable;
      return;
    }

    /* Smooth scrolling animates every step of a drag, which reads as lag. The
       rail gets its own behaviour back on release. */
    railDrag = {
      rail: rail,
      bar: bar,
      grabX: event.clientX,
      startLeft: rail.scrollLeft,
      behavior: rail.style.scrollBehavior
    };
    rail.style.scrollBehavior = 'auto';
    document.body.classList.add('mto-rail-dragging');
    if (bar.setPointerCapture) bar.setPointerCapture(event.pointerId);
  });

  document.addEventListener('pointermove', function (event) {
    var drag = railDrag;
    if (!drag) return;
    var travel = thumbTravel(drag.bar);
    if (travel <= 0) return;
    var scrollable = drag.rail.scrollWidth - drag.rail.clientWidth;
    drag.rail.scrollLeft = drag.startLeft + ((event.clientX - drag.grabX) / travel) * scrollable;
  });

  function endRailDrag(event) {
    var drag = railDrag;
    if (!drag) return;
    railDrag = null;
    drag.rail.style.scrollBehavior = drag.behavior || '';
    document.body.classList.remove('mto-rail-dragging');
    if (event && drag.bar.releasePointerCapture && drag.bar.hasPointerCapture(event.pointerId)) {
      drag.bar.releasePointerCapture(event.pointerId);
    }
  }

  document.addEventListener('pointerup', endRailDrag);
  document.addEventListener('pointercancel', endRailDrag);

  /* Hovering a card previews its next image, the way the comp reads. */
  document.addEventListener('mouseover', function (event) {
    var media = event.target.closest('.mto-card__media');
    if (!media) return;
    var card = media.closest('.mto-card');
    if (!card || card.querySelectorAll('.mto-card__frame').length < 2) return;
    showFrame(card, 1);
  });

  document.addEventListener('mouseout', function (event) {
    var media = event.target.closest('.mto-card__media');
    if (!media || (event.relatedTarget && media.contains(event.relatedTarget))) return;
    var card = media.closest('.mto-card');
    if (card) showFrame(card, 0);
  });

  document.addEventListener(
    'scroll',
    function (event) {
      var rail = event.target.closest && event.target.closest('[data-mto-rail]');
      if (!rail) return;
      syncScrollbar(rail);
      /* the arrows grey out at the ends, and a swipe or a drag reaches those
         ends without ever going through them — so they are read back here */
      railGo(rail, 0);
    },
    true
  );

  window.addEventListener('resize', resetRails);
  document.addEventListener('shopify:section:load', function () {
    syncWishlist();
    resetRails();
    startFeatureArrows();
  });

  syncWishlist();
  resetRails();
  startFeatureArrows();
})();
